/**
 * Leadership Dashboard — Story 7h.
 *
 * Client-side aggregation over the same admin-scoped views the other Phase 7
 * surfaces use — no new module data required. KPI tiles + a per-orientation
 * breakdown table. "Lightly loaded; first descope candidate" per the hookup
 * plan, so we keep it deliberately small: only the rollups that have real
 * signal today, no bespoke charts, no per-row drilldowns.
 *
 * Data sources (all admin-scoped server-side):
 *   - cohort_progress              → Assignment status counts, completion %.
 *   - cohort_step_metrics          → rushed-flag rate (mirrors ReviewQueue).
 *   - useUsers (visible_*_users)   → workforce headcount.
 *   - useOrientations              → orientation catalog for joining names.
 *   - useLiveSessions              → in-flight count (already filters + joins).
 */

import { useMemo } from 'react';
import { useSpacetimeDB } from 'spacetimedb/react';
import { useCohortProgress } from '@/hooks/useCohortProgress';
import { useCohortStepMetrics } from '@/hooks/useCohortStepMetrics';
import { useOrientationSteps } from '@/hooks/useOrientationSteps';
import { useOrientations } from '@/hooks/useOrientations';
import { useLiveSessions } from '@/hooks/useLiveSessions';
import { useUsers } from '@/hooks/useUsers';
import { useProjects } from '@/hooks/useProjects';
import { useTweaks } from '@/hooks/useTweaks';
import { NoSiteMembership } from '@/components/NoSiteMembership';
import { useMySiteMembership } from '@/hooks/useMySiteMembership';
import { pairingIdsOf, inCohortFilter } from '@/lib/cohortFilter';
import { CohortFilterSelect } from '@/components/CohortFilterSelect';
import { useCohortEntities } from '@/hooks/useCohortEntities';
import { useState } from 'react';
import type { Site, Toast } from '@/data/types';

type LeadershipProps = { toast: (t: Omit<Toast, 'id'>) => void };

/** Mirrors ReviewQueue's RUSHED_RATIO so rates compare apples-to-apples. */
const RUSHED_RATIO = 0.5;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function Leadership(_props: LeadershipProps) {
  const minetwinState = useSpacetimeDB('minetwin');
  const { rows: progress, isReady: progressReady } = useCohortProgress();
  const { rows: metrics, isReady: metricsReady } = useCohortStepMetrics();
  const { byStepId: stepByStepId, isReady: stepsReady } = useOrientationSteps();
  const { orientations } = useOrientations();
  const { sessions: live } = useLiveSessions();
  const { users, isReady: usersReady } = useUsers();
  const { sites } = useProjects();
  const { tweaks } = useTweaks();

  const currentSite: Site | undefined = sites[tweaks.siteIndex] ?? sites[0];
  const membership = useMySiteMembership(currentSite?.projectName ?? '');
  const { cohorts } = useCohortEntities(currentSite?.projectName);
  const [cohortFilter, setCohortFilter] = useState<bigint | null>(null);

  const orientationNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of orientations) m.set(o.orientationId, o.displayName);
    return m;
  }, [orientations]);

  const connOnline = minetwinState.isActive;
  const isReady = progressReady && metricsReady && stepsReady && usersReady;

  // Cohort filter (Phase 4, req 3): every assignment-derived number on
  // this page — rollup, per-orientation table, in-flight KPI — respects
  // it. Workforce headcount is roster-derived and deliberately does not.
  const filteredProgress = useMemo(() => {
    const ids = pairingIdsOf(cohorts, cohortFilter);
    if (ids == null) return progress;
    return progress.filter((a) => inCohortFilter(a.cohortAssignmentId ?? null, ids));
  }, [progress, cohorts, cohortFilter]);

  const filteredMetrics = useMemo(() => {
    if (filteredProgress === progress) return metrics;
    const keep = new Set(filteredProgress.map((a) => a.assignmentId.toString()));
    return metrics.filter((m) => keep.has(m.assignmentId.toString()));
  }, [metrics, filteredProgress, progress]);

  const rollup = useMemo(
    () => computeRollup(filteredProgress, filteredMetrics, stepByStepId),
    [filteredProgress, filteredMetrics, stepByStepId],
  );

  const perOrientation = useMemo(
    () => computePerOrientation(filteredProgress, orientationNameById),
    [filteredProgress, orientationNameById],
  );

  const workforce = useMemo(() => {
    const bound = users.filter((u) => u.bound).length;
    const pending = users.length - bound;
    return { total: users.length, bound, pending };
  }, [users]);

  const inFlightCount = useMemo(() => {
    const ids = pairingIdsOf(cohorts, cohortFilter);
    if (ids == null) return live.length;
    return live.filter((s) => inCohortFilter(s.cohortAssignmentId, ids)).length;
  }, [live, cohorts, cohortFilter]);

  if (membership.isReady && currentSite && !membership.isMember)
    return <NoSiteMembership pageName="Leadership" siteName={currentSite.name} />;

  return (
    <>
      <div className="page-head">
        <div>
          <h1 style={{ margin: 0, fontSize: 18 }}>Leadership</h1>
          <div style={{ color: 'var(--muted)', fontSize: 12, marginTop: 2 }}>
            {currentSite ? <>Org rollup · {currentSite.name}</> : <>Org rollup</>}
            {connOnline ? null : <span style={{ color: 'var(--bad)' }}> · offline</span>}
          </div>
        </div>
        <div className="page-actions">
          <CohortFilterSelect
            cohorts={cohorts}
            value={cohortFilter}
            onChange={setCohortFilter}
          />
        </div>
      </div>

      <div className="page-body">
        {!isReady ? (
          <div className="card" style={{ padding: 20, textAlign: 'center', color: 'var(--muted)', fontSize: 12.5 }}>
            Loading rollup…
          </div>
        ) : (
          <>
            <div style={kpiGridStyle}>
              <KpiTile
                label="In flight"
                value={inFlightCount}
                hint="Active orientations right now"
                tone="good"
              />
              <KpiTile
                label="Completed (7d)"
                value={rollup.completedLast7d}
                hint={`${rollup.completedLast30d} in last 30 days`}
              />
              <KpiTile
                label="Workforce"
                value={workforce.total}
                hint={`${workforce.bound} signed-in · ${workforce.pending} pending`}
              />
              <KpiTile
                label="Rushed steps"
                value={`${rollup.rushedPct.toFixed(0)}%`}
                hint={`${rollup.rushedSteps} of ${rollup.metricsScored} measured`}
                tone={rollup.rushedPct >= 25 ? 'warn' : undefined}
              />
            </div>

            <div className="card" style={{ marginTop: 10 }}>
              <div className="card-head" style={{ padding: '10px 12px' }}>
                <h3 style={{ margin: 0, fontSize: 13 }}>Per orientation</h3>
              </div>
              {perOrientation.length === 0 ? (
                <div style={{ padding: 18, textAlign: 'center', color: 'var(--muted)', fontSize: 12 }}>
                  No assignments yet. Assign an orientation in <b>Orientation Assignment</b>.
                </div>
              ) : (
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Orientation</th>
                      <th style={{ textAlign: 'right' }}>Assigned</th>
                      <th style={{ textAlign: 'right' }}>In progress</th>
                      <th style={{ textAlign: 'right' }}>Completed</th>
                      <th style={{ textAlign: 'right' }}>Cancelled</th>
                      <th style={{ textAlign: 'right' }}>Completion %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {perOrientation.map((row) => (
                      <tr key={row.orientationId}>
                        <td>
                          <b>{row.name}</b>
                          {row.name !== row.orientationId ? (
                            <div className="mono" style={{ fontSize: 11, color: 'var(--muted)' }}>
                              {row.orientationId}
                            </div>
                          ) : null}
                        </td>
                        <td style={numCellStyle}>{row.total}</td>
                        <td style={numCellStyle}>{row.inProgress}</td>
                        <td style={numCellStyle}>{row.completed}</td>
                        <td style={numCellStyle}>{row.cancelled}</td>
                        <td style={numCellStyle}>
                          <CompletionPct pct={row.completionPct} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ---------- helpers + UI atoms ----------

const kpiGridStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
  gap: 8,
};

const numCellStyle: React.CSSProperties = {
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
};

function KpiTile({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: number | string;
  hint?: string;
  tone?: 'good' | 'warn';
}) {
  const accent =
    tone === 'good' ? 'var(--good)' : tone === 'warn' ? 'var(--warn)' : 'var(--text)';
  return (
    <div
      className="card"
      style={{
        padding: '10px 12px',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}
    >
      <div style={{ fontSize: 10.5, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 700, color: accent, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>
        {value}
      </div>
      {hint ? (
        <div style={{ fontSize: 11, color: 'var(--muted)' }}>{hint}</div>
      ) : null}
    </div>
  );
}

function CompletionPct({ pct }: { pct: number }) {
  const tone =
    pct >= 80 ? 'var(--good)' : pct >= 40 ? 'var(--text)' : 'var(--warn)';
  return (
    <span style={{ color: tone, fontWeight: 600 }}>
      {pct.toFixed(0)}%
    </span>
  );
}

type RollupAssignment = ReturnType<typeof useCohortProgress>['rows'][number];
type RollupMetric = ReturnType<typeof useCohortStepMetrics>['rows'][number];

function computeRollup(
  progress: readonly RollupAssignment[],
  metrics: readonly RollupMetric[],
  stepByStepId: ReturnType<typeof useOrientationSteps>['byStepId'],
) {
  const now = Date.now();
  let completedLast7d = 0;
  let completedLast30d = 0;

  for (const a of progress) {
    if (a.status.tag !== 'Completed') continue;
    const completedAtMs = a.completedAt ? toMillis(a.completedAt) : 0;
    if (completedAtMs === 0) continue;
    const ageMs = now - completedAtMs;
    if (ageMs <= 30 * MS_PER_DAY) completedLast30d += 1;
    if (ageMs <= 7 * MS_PER_DAY) completedLast7d += 1;
  }

  let rushedSteps = 0;
  let metricsScored = 0;
  for (const m of metrics) {
    const actualSeconds =
      typeof m.timeSpentSeconds === 'number' ? m.timeSpentSeconds : null;
    if (actualSeconds === null) continue;
    const step = stepByStepId.get(m.stepId);
    const expectedSeconds = (step?.durationMinutes ?? 0) * 60;
    if (expectedSeconds <= 0) continue;
    metricsScored += 1;
    if (actualSeconds < expectedSeconds * RUSHED_RATIO) rushedSteps += 1;
  }
  const rushedPct = metricsScored === 0 ? 0 : (100 * rushedSteps) / metricsScored;

  return {
    completedLast7d,
    completedLast30d,
    rushedSteps,
    metricsScored,
    rushedPct,
  };
}

type PerOrientationRow = {
  orientationId: string;
  name: string;
  total: number;
  inProgress: number;
  completed: number;
  cancelled: number;
  completionPct: number;
};

function computePerOrientation(
  progress: readonly RollupAssignment[],
  nameById: Map<string, string>,
): PerOrientationRow[] {
  const byOrientation = new Map<string, PerOrientationRow>();
  for (const a of progress) {
    let row = byOrientation.get(a.orientationId);
    if (!row) {
      row = {
        orientationId: a.orientationId,
        name: nameById.get(a.orientationId) ?? a.orientationId,
        total: 0,
        inProgress: 0,
        completed: 0,
        cancelled: 0,
        completionPct: 0,
      };
      byOrientation.set(a.orientationId, row);
    }
    row.total += 1;
    switch (a.status.tag) {
      case 'InProgress':
        row.inProgress += 1;
        break;
      case 'Completed':
        row.completed += 1;
        break;
      case 'Cancelled':
        row.cancelled += 1;
        break;
    }
  }

  const rows = Array.from(byOrientation.values());
  for (const row of rows) {
    // Completion % excludes cancelled — they're not "still trying".
    const eligible = row.total - row.cancelled;
    row.completionPct = eligible === 0 ? 0 : (100 * row.completed) / eligible;
  }
  rows.sort((a, b) => b.total - a.total);
  return rows;
}

function toMillis(t: unknown): number {
  if (typeof t === 'number') return t;
  if (t instanceof Date) return t.getTime();
  if (t && typeof t === 'object') {
    const anyT = t as { micros?: bigint; getTime?: () => number };
    if (typeof anyT.getTime === 'function') return anyT.getTime();
    if (typeof anyT.micros === 'bigint') return Number(anyT.micros / 1000n);
  }
  return 0;
}
